/* SpawnAnything — mode "Verteidigung": everything the sim needs at `setup` (no DOM, also used by the Node tests).
   - one army bundle per wave (buildArmy for team 1; cached, the renderer swaps to it when the wave starts)
   - the building catalog: price, upgrade prices, income and level data for each rotation, built at the origin
     (the sim moves it to the bought plot with wavesPlaced, so every client derives the same data)
   - the wave list with the spawn edges resolved to numbers */
import { buildArmy } from '../game/voxel.js';
import { buildingSimData, compiledFor } from '../game/buildingSetup.js';
import { RESERVE_MAX } from '../sim/buildingRules.js';
import { WAVES, WAVES_CONFIG, WAVES_SIDES, wavesMaxCount } from '../content/waves.js';

const armies = new Map(), catalog = new Map();
const cfgOf = a => ({ type: a.type, count: 0, weapon: a.weapon, size: a.size || 1, t: 0.4, width: 1, head: 1 });

/** army bundle (meshes + stats) of a wave's enemy, built once per type/weapon/size */
export function wavesArmy(w) {
  const a = w.army, id = a.type + '|' + a.weapon + '|' + (a.size || 1);
  let A = armies.get(id);
  if (!A) { A = buildArmy(1, cfgOf(a)); armies.set(id, A); }
  return A;
}
/** the first wave's army config (team 1 at setup: no units yet, they come with the wave) */
export const wavesEnemyCfg = (waves = WAVES) => cfgOf(waves[0].army);

/** catalog entry of a building recipe key (cached; recipes under a key never change) */
export function wavesCatalogEntry(key, recipeOf) {
  let e = catalog.get(key);
  if (!e) {
    const rec = recipeOf(key), st = [1, 2, 3].map(L => compiledFor(key, rec, L).stats);
    const rots = buildingSimData([0, 1, 2, 3].map(rot => ({ key, team: 0, level: 1, x: 0, z: 0, rot })), recipeOf)
      .map(d => ({ yaw: d.yaw, hw: d.hw, hd: d.hd, lv: d.lv }));
    e = { cost: st[0].cost | 0, up: st[0].upgradeCost.map(c => c | 0), income: st.map(s => s.income | 0), rots };
    catalog.set(key, e);
  }
  return e;
}

/** the core building as a placement for buildingSimData / placedB (building id 0 in the sim) */
export const wavesCore = (config = WAVES_CONFIG) => ({ key: config.core.key, team: 0, level: 1, x: config.core.x, z: config.core.z, rot: config.core.rot });

/**
 * The `waves` block of the worker's setup message and the reserved unit slots.
 * @param {{ keys: string[], recipeOf: (k: string) => object, waves?: object[], config?: object, core?: number }} o
 *   keys = buildable recipe keys (the core's key is added for its upgrade prices)
 */
export function wavesSetup({ keys, recipeOf, waves = WAVES, config = WAVES_CONFIG, core = 0 }) {
  const cat = {};
  for (const k of new Set([config.core.key, ...keys])) cat[k] = wavesCatalogEntry(k, recipeOf);
  const stats = waves.map(w => {
    const S = wavesArmy(w).stats, hpMul = w.army.hpMul || 1;
    return { ...S, count: 0, hp: S.hp * hpMul };
  });
  const sides = s => { const d = WAVES_SIDES[s]; return [d.x, d.z, d.alongX ? 1 : 0, d.half]; };
  return {
    reserve: [RESERVE_MAX, wavesMaxCount(waves)],
    waves: {
      waves: waves.map(w => ({ count: w.army.count, bounty: w.bounty, bonus: w.bonus, from: w.from.map(sides), spawn: w.spawnSeconds })),
      armies: stats, catalog: cat, core, target: [config.core.x, config.core.z],
      startGold: config.startGold, first: config.firstBuildSeconds, build: config.buildSeconds, early: config.earlyGoldPerSecond,
      area: config.area.slice(), maxBuilt: config.maxBuilt,
    },
  };
}
